import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { SEO } from "@/components/seo";

const faqData = [
  {
    category: "General",
    questions: [
      {
        q: "What is Sierra Chart Pro?",
        a: "Sierra Chart Pro provides premium chart templates and custom indicators designed specifically for Sierra Chart. Our products are built by professional traders to help you analyze markets more effectively with pre-configured layouts, advanced indicators, and professional-grade tools.",
      },
      {
        q: "What is Sierra Chart?",
        a: "Sierra Chart is a professional trading and charting platform known for its speed, reliability, and advanced charting capabilities. It supports multiple data feeds and is widely used by futures, forex, and stock traders for its powerful analysis tools.",
      },
      {
        q: "Do I need Sierra Chart to use your products?",
        a: "Yes, all of our templates and indicators are designed exclusively for the Sierra Chart platform. You will need an active Sierra Chart license to use our products.",
      },
    ],
  },
  {
    category: "Products & Purchasing",
    questions: [
      {
        q: "What types of products do you offer?",
        a: "We offer two main categories: Templates and Indicators. Templates include pre-configured chart layouts such as Market Profile TPO, Footprint Charts, Volume Profiles, Heatmaps, and DOM setups. Indicators include custom studies like Orderflow Suite, Absorption indicators, and Volume Sequence tools.",
      },
      {
        q: "Are these one-time purchases or subscriptions?",
        a: "All of our products are one-time purchases. Once you buy a template or indicator, it's yours to keep and use forever. There are no recurring fees or subscription charges.",
      },
      {
        q: "Can I use the products on multiple computers?",
        a: "Yes, our products are licensed per user, not per machine. You can install them on any computer where you have Sierra Chart installed, as long as it is for your personal use.",
      },
      {
        q: "Do you offer refunds?",
        a: "Due to the digital nature of our products, we generally do not offer refunds once a product has been downloaded. If you experience technical issues, please contact us and we'll work to resolve them. If a product is genuinely defective, we will issue a full refund.",
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit and debit cards (Visa, Mastercard, American Express, Discover) through our secure Stripe payment processing. All transactions are encrypted and secure.",
      },
    ],
  },
  {
    category: "Installation & Setup",
    questions: [
      {
        q: "How do I install a template?",
        a: "After purchasing, download the template file from your Purchases page. Open Sierra Chart, go to File > Open Chartbook, and select the downloaded file. The template will load with all pre-configured settings, layouts, and studies.",
      },
      {
        q: "How do I install a custom indicator?",
        a: "Download the indicator file from your Purchases page. Place the .dll file in your Sierra Chart Data folder (typically C:\\SierraChart\\Data). Restart Sierra Chart, then add the study to your chart via Analysis > Studies. Detailed instructions are provided with each product.",
      },
      {
        q: "Will the templates overwrite my existing charts?",
        a: "No. Templates open as new chartbooks and will not affect your existing setups. You can run them alongside your current charts without any conflicts.",
      },
      {
        q: "What Sierra Chart version do I need?",
        a: "Our products are designed for the latest stable release of Sierra Chart. We recommend keeping Sierra Chart updated to the most recent version to ensure full compatibility. Specific version requirements, if any, are noted on each product page.",
      },
    ],
  },
  {
    category: "Account & Downloads",
    questions: [
      {
        q: "How do I access my purchased products?",
        a: "After completing your purchase, go to the Purchases page in your account. All your purchased products will be listed there with download links. You can re-download products at any time.",
      },
      {
        q: "Is there a download limit?",
        a: "There is no limit on how many times you can download your purchased products. Re-download anytime from your Purchases page.",
      },
      {
        q: "I forgot my password. How do I reset it?",
        a: "Click the 'Forgot password?' link on the login page and enter your email address. You'll receive a password reset email with a link to create a new password. The link expires after 1 hour for security.",
      },
      {
        q: "Can I change my email address?",
        a: "Currently, email changes are not self-service. Please contact us at support and we'll help you update your account email.",
      },
    ],
  },
  {
    category: "Technical Support",
    questions: [
      {
        q: "A template or indicator isn't working correctly. What should I do?",
        a: "First, make sure you're running the latest version of Sierra Chart. Try re-downloading the product from your Purchases page and reinstalling. If the issue persists, contact us with details about the problem, your Sierra Chart version, and any error messages you see.",
      },
      {
        q: "Do you offer customization services?",
        a: "Yes, we can create custom templates and indicators tailored to your specific trading needs. Contact us to discuss your requirements and get a quote.",
      },
      {
        q: "How do I contact support?",
        a: "You can reach us via email. We aim to respond to all inquiries within 24 hours during business days.",
      },
    ],
  },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-border last:border-b-0" data-testid={`faq-item-${question.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4 px-1 text-left hover:text-primary transition-colors"
        data-testid="button-faq-toggle"
      >
        <span className="font-medium text-sm sm:text-base pr-4">{question}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="pb-4">
          <p className="text-sm text-muted-foreground leading-relaxed px-1">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <SEO
        title="Frequently Asked Questions"
        description="Find answers to common questions about Sierra Chart Pro templates, indicators, installation, purchasing, and technical support."
        keywords="Sierra Chart FAQ, trading templates help, indicator installation, Sierra Chart support"
        canonical="/faq"
      />

      <h1 className="text-2xl sm:text-3xl font-bold mb-2">Frequently Asked Questions</h1>
      <p className="text-muted-foreground mb-8">
        Find answers to common questions about our products, installation, and support.
      </p>

      <div className="space-y-8">
        {faqData.map((section) => (
          <div key={section.category}>
            <h2 className="text-lg font-semibold text-primary mb-3" data-testid={`text-faq-category-${section.category.toLowerCase().replace(/\s+/g, '-')}`}>
              {section.category}
            </h2>
            <div className="rounded-lg border border-border bg-card">
              {section.questions.map((item) => (
                <FAQItem key={item.q} question={item.q} answer={item.a} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
